import React from 'react';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import Name from 'pages/Dashboard/components/Name';
import {
  formatDate,
  formatDateTime,
} from 'utils/date';
import {
  BulkPatientStatementDTO,
} from 'dtos/bulkPatientStatementDTO';
import {
  fileStatusColumn,
} from 'pages/Dashboard/pages/Reports/columns';
import {
  useUserProfilePicturePath,
} from 'pages/Dashboard/hooks/media';
import {
  Row,
} from '@tanstack/react-table';
import {
  RunActionsCell,
  RunSummaryLine,
} from 'views/EMR/components/Panel/RunOutputPanel/tableCells';
import type {
  ReportState,
} from 'views/EMR/components/Panel/RunOutputPanel/types';

type RequestedByUserCellProps = {
  row: Row<BulkPatientStatementDTO>;
};

function RequestedByUserCell({
  row: { original: { requestedByUserDTO } },
}: RequestedByUserCellProps) {
  const src = useUserProfilePicturePath(requestedByUserDTO?.userId);

  return (
    <Name
      {...requestedByUserDTO?.formattedNames}
      image={{ src }}
    />
  );
}

type StatementRunActionsCellProps = {
  row: Row<BulkPatientStatementDTO>;
};

function StatementRunActionsCell({ row }: StatementRunActionsCellProps) {
  const {
    printStatementReportId,
    reportState,
    fileCount,
    isFileMissing,
    fileMissingReason,
  } = row.original;

  return (
    <RunActionsCell
      source={{ kind: 'statement', bulkPatientStatementId: printStatementReportId ?? 0 }}
      state={reportState as ReportState | null | undefined}
      fileCount={fileCount ?? 0}
      isFileMissing={isFileMissing}
      fileMissingReason={fileMissingReason}
    />
  );
}

const columns: ColumnDef<BulkPatientStatementDTO>[] = [
  fileStatusColumn,
  {
    id: 'parameters',
    accessorKey: 'parameters',
    columnTitle: 'Parameters',
    meta: { autoSized: true },
    cell: ({
      row: {
        original: {
          startDate,
          endDate,
          reportState,
          fileCount,
          exceptionCount,
        },
      },
    }) => (
      <div className="font-semibold">
        <span>
          {[formatDate(startDate ?? ''), formatDate(endDate ?? '')].join(' to ')}
        </span>
        <RunSummaryLine
          state={reportState as ReportState | null | undefined}
          fileCount={fileCount ?? 0}
          exceptionCount={exceptionCount ?? 0}
        />
      </div>
    ),
  }, {
    id: 'createdOn',
    accessorKey: 'createdOn',
    columnTitle: 'Created on',
    size: 80,
    cell: ({ row: { original: { createdOn } } }) => (
      formatDateTime(createdOn ?? '')
    ),
  }, {
    id: 'requestedByUserDTO',
    accessorKey: 'requestedByUserDTO',
    columnTitle: 'Created by',
    size: 220,
    cell: ({ row }) => <RequestedByUserCell row={row} />,
  }, {
    id: 'actions',
    columnTitle: '',
    size: 88,
    cell: ({ row }) => <StatementRunActionsCell row={row} />,
  }];

export default columns;
